// onshape-ref.js — canonical onshape_ref format + write-validation (Phase 1a).
//
// Canonical format: `did/wid/eid` — slash-separated, EXACTLY 3 segments, each a
// 16-32 char hex-ish OnShape id. See ONSHAPE_LINKING.md at the repo root for the
// full spec (segment meanings, where refs live, the ↗ link, and the audit query).
//
// NO partId (RULES.md rule 10 / Alex 2026-07-15). A `pid` used to be allowed as a
// 4th segment. It is now rejected, because **partId is not an identity** — it is a
// handle scoped to the response that produced it. Proven live: the same 4 elbows,
// in the same element, came back as RPCD/RQCD/RRCD/RSCD from the metadata endpoint
// and RtBD/RuBD/RrBD/RsBD from the BOM. Two views of one instant that disagree.
// Storing one is storing an address as if it were a name; it would silently point
// at the wrong part (or nothing) later. The durable key is (db_id, config_id),
// which lives in Onshape as part metadata — not in this ref.
//
// Shared by product-db.js (products.onshape_ref) and catalog-db.js
// (catalog_lines.onshape_ref) so both tables validate identically.

const SEGMENT_RE = /^[a-f0-9]{16,32}$/i;

export const INVALID_ONSHAPE_REF_MESSAGE =
  'Invalid Onshape ref — expected did/wid/eid (copy from the Onshape URL).';

// isValidOnshapeRef(ref): true only for a non-empty string with EXACTLY 3
// slash-separated segments, each matching SEGMENT_RE. A 4th (partId) segment is
// rejected — see the rule-4 note above.
export function isValidOnshapeRef(ref) {
  if (typeof ref !== 'string') return false;
  const parts = ref.trim().split('/');
  if (parts.length !== 3) return false;
  return parts.every(p => SEGMENT_RE.test(p));
}

// normalizeOnshapeRef(input): accept what a human actually has in hand — a
// PASTED ONSHAPE URL — and return the canonical `did/wid/eid`. Alex hit this
// 2026-07-13: pasting the URL straight from the browser was rejected and had to
// be trimmed by hand, which is silly when the ids are right there.
// Handles /w/ (workspace), /v/ (version) and /m/ (microversion) URLs, with or
// without query params. An already-canonical ref is returned unchanged; anything
// unrecognized is returned as-is so validation still rejects it with the same
// message.
//
// A trailing `/p/{pid}` is matched and DROPPED rather than rejected: pasting a
// part URL is a reasonable thing to do, and the element it names is exactly what
// we want — but the partId itself must not survive the paste (rule 4 above).
export function normalizeOnshapeRef(input) {
  if (input == null) return '';
  const s = String(input).trim();
  if (s === '' || isValidOnshapeRef(s)) return s;
  const m = /documents\/([a-f0-9]{16,32})\/[wvm]\/([a-f0-9]{16,32})\/e\/([a-f0-9]{16,32})(?:\/p\/[a-f0-9]{16,32})?/i.exec(s);
  if (!m) return s;
  const [, did, wid, eid] = m;
  return `${did}/${wid}/${eid}`;
}

// normalizeOnshapeRefOrThrow(value): normalize a pasted URL/ref, validate it,
// and hand back the canonical form to STORE (or '' to clear the link). One call
// for every write path so a pasted URL works everywhere identically.
export function normalizeOnshapeRefOrThrow(value) {
  const norm = normalizeOnshapeRef(value);
  assertValidOnshapeRefOrEmpty(norm);
  return norm;
}

// assertValidOnshapeRefOrEmpty(value): throws on a non-empty, malformed ref.
// Empty/null/undefined is allowed (clears the link) — callers normalize that
// to NULL before storing.
export function assertValidOnshapeRefOrEmpty(value) {
  if (value == null) return;
  const trimmed = String(value).trim();
  if (trimmed === '') return;
  if (!isValidOnshapeRef(trimmed)) throw new Error(INVALID_ONSHAPE_REF_MESSAGE);
}

// ---------------------------------------------------------------------------
// NPS <-> Onshape config option ID convention (family-level linking, locked
// 2026-07-03 — see ONSHAPE_LINKING.md "Configurable parts — family linking").
//
// Onshape enum config option internal IDs for the `Size` input are named
// `NPS` + zero-padded 2-digit whole-inch part, plus an underscore fraction
// suffix for halves/quarters (the decimal point becomes `_`):
//   6    -> NPS06        2.5  -> NPS02_5      0.75 -> NPS00_75
//   24   -> NPS24        1.25 -> NPS01_25     10   -> NPS10
// ---------------------------------------------------------------------------

// npsOptionId(sizeNps): number -> Onshape option ID string, or null if not a
// clean whole/half/quarter size (>=0, <100 so the 2-digit pad holds).
export function npsOptionId(sizeNps) {
  const n = Number(sizeNps);
  if (!Number.isFinite(n) || n < 0 || n >= 100) return null;
  const whole = Math.trunc(n);
  const frac = Math.round((n - whole) * 100); // 0, 25, 50, or 75
  if (![0, 25, 50, 75].includes(frac)) return null; // not a clean quarter
  const wholeStr = String(whole).padStart(2, '0');
  if (frac === 0) return `NPS${wholeStr}`;
  if (frac === 50) return `NPS${wholeStr}_5`;
  return `NPS${wholeStr}_${frac}`; // 25 -> _25, 75 -> _75
}

// parseNpsOptionId(optionId): reverse of npsOptionId — Onshape option ID
// string -> number, or null if it doesn't match the convention.
export function parseNpsOptionId(optionId) {
  if (typeof optionId !== 'string') return null;
  const m = /^NPS(\d{2})(?:_(5|25|75))?$/.exec(optionId.trim());
  if (!m) return null;
  const whole = Number(m[1]);
  // '_5' is shorthand for the half (50), not five-hundredths — matches the
  // npsOptionId encoding above (frac===50 -> '_5').
  const fracDigits = m[2] === '5' ? 50 : m[2] ? Number(m[2]) : 0;
  return whole + fracDigits / 100;
}

// sizeConfigParam(sizeNps): the `Size=NPSxx` config axis for one size, or null
// if sizeNps doesn't map to a clean option ID.
export function sizeConfigParam(sizeNps) {
  const opt = npsOptionId(sizeNps);
  return opt ? `Size=${opt}` : null;
}

// sizeAxisValue(sizeId): the value a GENERALIZED size axis sends — the
// `size_id`, which IS the Onshape option ID (`NPS02_5`, `C02`, `R3`), never
// `size_lists.look`, which is the DISPLAY text (`2 1/2" (65)`, `2m`, `3`).
//
// Alex, 2026-08-01, on the roller beds: *"for the roller bed (RBS RBX), it is
// R3, R4, etc. I have hard time to get the link working."* The generalized
// branch was being handed the look, so RBS R3 sent `Size=3` and Onshape had no
// such option. 26 lines across 6 families were affected — and not only the
// generalized ones read wrong: Pipe—304SS sent `Size=2 1/2" (65)`.
//
// Using size_id also makes the two branches AGREE rather than diverge: for an
// NPS ladder the size_id is exactly what npsOptionId derives (`NPS02_5`).
//
// `'unique'` is the no-size-axis sentinel (a real size_lists row so that
// (family, size) is always a key, never something to pick), so it resolves to
// null and the caller falls back to the base-only config.
export function sizeAxisValue(sizeId) {
  const v = sizeId == null ? '' : String(sizeId).trim();
  if (!v || v === 'unique') return null;
  return v;
}

// buildFamilyConfig(configBase, sizeNps, sizeParam?, sizeOption?): combine the
// family's non-size config axes with the derived per-size axis. Returns null
// if the size can't be resolved to a config option.
//
// Generalized size axis (2026-07-04, catalog batch — see ONSHAPE_LINKING.md
// "Generalized size axis"): when `sizeParam` is set (catalog_families.
// onshape_size, e.g. 'Type'), the per-size axis is `${sizeParam}=${sizeOption}`
// verbatim — no NPS derivation, no mapping table. Callers pass
// sizeAxisValue(size_id); see that function for why it is the id and not the
// display text. When `sizeParam` is NULL/undefined, behavior is UNCHANGED: the
// axis is the locked `Size=NPSxx` convention derived from sizeNps.
export function buildFamilyConfig(configBase, sizeNps, sizeParam, sizeOption) {
  const base = (configBase == null ? '' : String(configBase).trim());
  let sizePart;
  if (sizeParam) {
    const val = sizeOption == null ? '' : String(sizeOption).trim();
    if (!val) return null;
    sizePart = `${sizeParam}=${val}`;
  } else {
    sizePart = sizeConfigParam(sizeNps);
    if (!sizePart) return null;
  }
  return base ? `${base};${sizePart}` : sizePart;
}

// ---------------------------------------------------------------------------
// Config matching (2026-07-16). One Onshape ELEMENT hosts MANY families: element
// 6244dd4b… is both fam#35 "Flange — CS" (Type=SO) and fam#43 "Flange — SS304"
// (Type=LJ). So `ref → family` is ambiguous and silently returns whichever row
// comes first. The CONFIGURATION is what disambiguates them.
//
// Compare PARSED PAIRS, never strings, for two independent reasons:
//   1. Onshape OMITS params sitting at their default — a BOM row reads
//      `Size=NPS02;Type=SO` where we derive `P_Class=C150;Type=SO;Face=Default;Size=NPS02`.
//   2. Order differs by producer — Alex's FeatureScript emits alphabetically
//      (`Face=…;P_Class=…;Size=…;Type=…`), buildFamilyConfig emits base-then-size.
// Parsed, both collapse to the same map, so neither difference matters. (Alex
// 2026-07-16 asked whether to fix the ordering in the FS or the DB: NEITHER —
// parse, don't compare text.)
// ---------------------------------------------------------------------------

// parseConfig('a=1;b=2') → { a: '1', b: '2' }. Tolerates ''/null/stray spaces.
export function parseConfig(config) {
  const out = {};
  for (const pair of String(config || '').split(';')) {
    const s = pair.trim();
    if (!s) continue;
    const i = s.indexOf('=');
    if (i <= 0) continue;
    out[s.slice(0, i).trim()] = s.slice(i + 1).trim();
  }
  return out;
}

// configAgrees(actual, expected): every param PRESENT in `actual` agrees with
// `expected`. Subset, not equality — because of the omitted-defaults rule above:
// an Onshape config carries only its non-default params, so it is legitimately a
// subset of the family's full base+size. Absent means "at its default", which
// cannot conflict. `'default'` (the derived-part marker) parses to {} and so
// agrees with everything — correctly: it carries NO information, which is
// precisely why derived parts need db_id/config_id at all.
export function configAgrees(actual, expected) {
  const a = parseConfig(actual);
  const e = parseConfig(expected);
  return Object.entries(a).every(([k, v]) => e[k] === undefined || e[k] === v);
}

// configSpecificity(config): how many params a config pins. Ranks candidates —
// the most specific agreeing match wins.
export function configSpecificity(config) {
  return Object.keys(parseConfig(config)).length;
}

// onshapeConfiguredUrl(onshapeRef, configBase, sizeNps, sizeParam?, sizeText?):
// full Onshape document URL with the `configuration` query param, encoded per
// Onshape's convention (';' -> %3B, '=' -> %3D) exactly as the URL bar shows
// it. Returns null if the ref is malformed or the size can't be resolved.
// String derivation only — no Onshape API calls, ever (see ONSHAPE_LINKING.md).
export function onshapeConfiguredUrl(onshapeRef, configBase, sizeNps, sizeParam, sizeText) {
  if (!isValidOnshapeRef(onshapeRef)) return null;
  const config = buildFamilyConfig(configBase, sizeNps, sizeParam, sizeText);
  if (!config) return null;
  return encodeConfiguredUrl(onshapeRef, config);
}

// buildBaseOnlyConfig(configBase): the family's non-size config axes alone,
// with NO size part — "open this family's Type at Onshape's default size" is
// the right family-level intent (Onshape applies its own defaults for any
// unspecified param). A sibling to buildFamilyConfig, not a replacement: that
// function's contract (null when sizeParam is set and size is empty) stays
// exactly as-is for its existing per-line callers. Returns null when
// configBase is empty — "no config to add" — so callers fall back to the
// plain doc URL instead of an empty `?configuration=`.
export function buildBaseOnlyConfig(configBase) {
  const base = (configBase == null ? '' : String(configBase).trim());
  return base || null;
}

// onshapeBaseConfiguredUrl(onshapeRef, configBase): same URL shape as
// onshapeConfiguredUrl, but carrying ONLY the base config (no size axis).
// Used for (a) the family-level "Open in Onshape" link (Bug 1 — the family
// panel has no single size to pin) and (b) the size-less-row fallback (Bug 2
// — better to open the right configuration at the default size than no link
// at all). Returns null if the ref is malformed or configBase is empty —
// callers fall back to the bare onshapeDocUrl in that case.
export function onshapeBaseConfiguredUrl(onshapeRef, configBase) {
  if (!isValidOnshapeRef(onshapeRef)) return null;
  const config = buildBaseOnlyConfig(configBase);
  if (!config) return null;
  return encodeConfiguredUrl(onshapeRef, config);
}

// encodeConfiguredUrl(onshapeRef, config): shared tail of both URL builders
// above — split the ref, encode the config per Onshape's convention, join.
function encodeConfiguredUrl(onshapeRef, config) {
  const parts = String(onshapeRef).trim().split('/');
  const [did, wid, eid] = parts;
  const encoded = config
    .split(';').map(seg => seg.split('=').map(encodeURIComponent).join('%3D')).join('%3B');
  return `https://cad.onshape.com/documents/${did}/w/${wid}/e/${eid}?configuration=${encoded}`;
}
